import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type * as Phaser from 'phaser';
import './App.css';
import GameCanvas, { GameUiState, DISCARD_REVEAL_DELAY_MS } from './component/GameCanvas';
import { Card, CARD_TYPES } from './component/Cards';
import { GameLogEntry, formatEntry, renderBoard, serializeLog } from './component/GameLog';
import { api } from './api';
import type { User } from './api';
import { HistoryPage, LoginPage, SignupPage } from './component/AuthPages';
import { Sidebar } from './component/Sidebar';
import { multiplayer, type MatchStart, type OnlineAction } from './multiplayer';
import cardBackArt from './assets/img/cards/card-back.svg';
import cardArt01 from './assets/img/cards/card-01.svg';
import cardArt02 from './assets/img/cards/card-02.svg';
import cardArt03 from './assets/img/cards/card-03.svg';
import cardArt04 from './assets/img/cards/card-04.svg';

const GAME_TITLE = 'TayTayA';
const CANVAS_SIZE = 800;
const APP_PADDING = 20;
const ROW_GAP = 24;
const STACK_GAP = 14;
const SIDEBAR_WIDTH = 240;
// each player's time bank (2 minutes, consumed only during their own turns)
const STARTING_TIME_MS = 2 * 60 * 1000;
// how long the "X will move first" banner stays over the board (must match the
// first-mover-overlay animations in src/App.css)
const FIRST_MOVER_OVERLAY_MS = 2000;

// card art lookup keyed by the `artKey` in CARD_TYPES
const CARD_ARTS: Record<string, string> = {
  'card-01': cardArt01,
  'card-02': cardArt02,
  'card-03': cardArt03,
  'card-04': cardArt04,
};

const INITIAL_STATE: GameUiState = {
  currentPlayer: 1,
  gameOver: false,
  winner: null,
  draw: false,
  hand1: [],
  hand2: [],
  deckCount: 30,
  discardCount: 0,
  board: [
    [null, null, null],
    [null, null, null],
    [null, null, null],
  ],
  lockedCells: [],
  targeting: false,
  targetingCardId: null,
  cardLockedPlayer: null,
  cardPlayedThisTurn: false,
  chainableCardId: null,
  cardDiscardedThisTurn: false,
  leavingCardId: null,
  gameMode: 'pvp',
  botPlayer: null,
  localPlayer: null,
  playerNames: { 1: 'Player 1', 2: 'Player 2' },
};

type ActivatingCard = { card: Card; player: number };
type CardMenu = { player: number; card: Card; x: number; y: number };

type TimerTick = { active: number; player1: number; player2: number };

// the live countdown for both players. It listens to the scene's `timer-tick`
// event (4x/second) and only re-renders itself, never the whole board. The
// LOCAL player's badge is always at the bottom (matching their hand/pieces).
function TimerBadges({
  game,
  botPlayer,
  playerNames,
  localPlayer,
}: {
  game: Phaser.Game | null;
  botPlayer: number | null;
  playerNames?: Record<number, string> | null;
  localPlayer?: number | null;
}) {
  const [times, setTimes] = useState({ player1: STARTING_TIME_MS, player2: STARTING_TIME_MS });
  const [active, setActive] = useState(1);
  useEffect(() => {
    if (!game) {
      return;
    }
    const onTick = (e: TimerTick) => {
      setTimes({ player1: e.player1, player2: e.player2 });
      setActive(e.active);
    };
    game.events.on('timer-tick', onTick);
    return () => {
      game.events.off('timer-tick', onTick);
    };
  }, [game]);
  const format = (ms: number) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  };
  const bottomPlayer = localPlayer ?? 1;
  const topPlayer = bottomPlayer === 1 ? 2 : 1;
  const slots = [
    { player: bottomPlayer, pos: 'bottom' },
    { player: topPlayer, pos: 'top' },
  ];
  return (
    <div className="timer-badges">
      {slots.map(({ player, pos }) => (
        <span
          key={player}
          className={`timer-badge timer-badge--p${player} timer-badge--${pos} ${
            active === player ? 'timer-badge--active' : ''
          }`}
        >
          <svg className="timer-badge__icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm1-13h-2v6l5.2 3.1 1-1.6-4.2-2.5V7z" />
          </svg>
          {playerNames?.[player] ?? (botPlayer === player ? 'BOT' : `P${player}`)} {format(player === 1 ? times.player1 : times.player2)}
        </span>
      ))}
    </div>
  );
}

function App() {
  const gameRef = useRef<Phaser.Game | null>(null);
  const [game, setGame] = useState<Phaser.Game | null>(null);
  // game mode selection: null shows the pre-game menu
  const [gameMode, setGameMode] = useState<'pvp' | 'bot' | 'online' | null>(null);
  // who moves first: picked at random for local modes, assigned by the server
  // for online/invite matches
  const [firstPlayer, setFirstPlayer] = useState<number | null>(null);
  // true while the "X will move first" banner is shown over the board; cleared
  // automatically once the animation finishes (~1s) - no confirmation needed
  const [firstMoverOverlay, setFirstMoverOverlay] = useState(false);
  const [uiState, setUiState] = useState<GameUiState>(INITIAL_STATE);
  const [drawnIds, setDrawnIds] = useState<Set<string>>(new Set());
  const [activatingCard, setActivatingCard] = useState<ActivatingCard | null>(null);
  const [logEntries, setLogEntries] = useState<GameLogEntry[]>([]);
  const [verbose, setVerbose] = useState(false);
  // side drawer (deck stats + history), toggled by the floating deck button
  const [drawerOpen, setDrawerOpen] = useState(false);
  // mobile menu drawer: the sidebar is hidden off-screen and slides in when
  // the hamburger button is tapped
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // online multiplayer: matchmaking lobby status, the server-granted match, and
  // whether the opponent has disconnected mid-match
  const [onlineStatus, setOnlineStatus] = useState<'idle' | 'connecting' | 'waiting' | 'error'>('idle');
  const [onlineMatch, setOnlineMatch] = useState<MatchStart | null>(null);
  const [opponentLeft, setOpponentLeft] = useState(false);
  // which online lobby is shown: server matchmaking, or an invite-only room
  const [onlineKind, setOnlineKind] = useState<'matchmake' | 'invite' | null>(null);
  // invite-only match: host creates a room + unique code, guest joins with it
  const [inviteStatus, setInviteStatus] = useState<
    'idle' | 'creating' | 'waiting' | 'join' | 'joining' | 'error'
  >('idle');
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [inviteJoinCode, setInviteJoinCode] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);

  // online matches: the local player's table (their hand + pieces) is always on
  // the bottom side. Side 1 keeps the canonical board; side 2 sees it flipped
  // (the Phaser scene rotates the camera for that client). Computed early so
  // the discard-reveal animation can measure the correct hand row.
  const isOnlineGame = gameMode === 'online';
  const localSide = isOnlineGame ? (onlineMatch?.side ?? null) : null;
  const bottomPlayer = localSide ?? 1;
  const topPlayer = bottomPlayer === 1 ? 2 : 1;
  const onlinePlayerNames =
    isOnlineGame && onlineMatch
      ? { [onlineMatch.side]: 'You', [onlineMatch.side === 1 ? 2 : 1]: onlineMatch.opponent }
      : undefined;

  // card action popup + description modal
  const [cardMenu, setCardMenu] = useState<CardMenu | null>(null);
  const [descCard, setDescCard] = useState<Card | null>(null);
  const [descFrom, setDescFrom] = useState<{ x: number; y: number } | null>(null);

  // the card revealed by a THROW CARD discard effect, plus where it flew from.
  // the reveal is delayed until the ACTIVATE overlay has finished playing
  const [discardingCard, setDiscardingCard] = useState<Card | null>(null);
  const [discardFrom, setDiscardFrom] = useState<{ x: number; y: number } | null>(null);
  const discardTimerRef = useRef<number | null>(null);

  // layout refs for canvas-only scaling
  const p2HandRef = useRef<HTMLDivElement>(null);
  const p1HandRef = useRef<HTMLDivElement>(null);
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [canvasScale, setCanvasScale] = useState(1);

  // account session + page navigation
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authView, setAuthView] = useState<'login' | 'signup'>('login');
  const [showHistory, setShowHistory] = useState(false);
  const logRef = useRef<GameLogEntry[]>([]);
  const savedGameRef = useRef(false);
  // live mirror of uiState for event handlers registered once (so they always
  // see the current game state without re-subscribing)
  const uiStateRef = useRef(uiState);
  uiStateRef.current = uiState;
  // live mirrors for the once-registered socket handlers
  const onlineKindRef = useRef(onlineKind);
  onlineKindRef.current = onlineKind;
  const inviteStatusRef = useRef(inviteStatus);
  inviteStatusRef.current = inviteStatus;
  // timer that hides the "X will move first" banner after its animation plays
  const firstMoverTimerRef = useRef<number | null>(null);

  const handleReady = (g: Phaser.Game) => {
    gameRef.current = g;
    setGame(g);
  };

  const handleRestart = () => {
    if (discardTimerRef.current !== null) {
      window.clearTimeout(discardTimerRef.current);
      discardTimerRef.current = null;
    }
    setDrawerOpen(false);
    setDrawnIds(new Set());
    setActivatingCard(null);
    setCardMenu(null);
    setDescCard(null);
    setDiscardingCard(null);
    setDiscardFrom(null);
    setLogEntries([]);
    logRef.current = [];
    savedGameRef.current = false;
    gameRef.current?.events.emit('restart-requested');
  };

  const handleModeSelect = (mode: 'pvp' | 'bot' | 'online') => {
    setGameMode(mode);
    setUiState((prev) => ({ ...prev, gameMode: mode, botPlayer: mode === 'bot' ? 2 : null }));
    if (mode === 'online') {
      setOnlineKind('matchmake');
      startOnlineLobby();
      return;
    }
    // local modes: no coin toss — the first mover is picked at random and
    // announced over the board before play begins
    setFirstPlayer(Math.random() < 0.5 ? 1 : 2);
    showFirstMoverOverlay();
  };

  const startOnlineLobby = async () => {
    setOpponentLeft(false);
    setOnlineMatch(null);
    setOnlineStatus('connecting');
    try {
      await multiplayer.connect();
      multiplayer.requestMatch();
      setOnlineStatus('waiting');
    } catch {
      setOnlineStatus('error');
    }
  };

  const cancelOnlineLobby = () => {
    multiplayer.cancelMatch();
    multiplayer.disconnect();
    setOnlineStatus('idle');
    setOnlineKind(null);
    setGameMode(null);
    setFirstPlayer(null);
  };

  // invite-only match: show the invite lobby (host creates / guest joins). The
  // socket connects but does not enter the matchmaking queue.
  const startInviteLobby = async () => {
    setOpponentLeft(false);
    setOnlineMatch(null);
    setGameMode('online');
    setOnlineKind('invite');
    setInviteStatus('idle');
    setInviteCode(null);
    setInviteJoinCode('');
    setInviteError(null);
    setOnlineStatus('connecting');
    try {
      await multiplayer.connect();
      setOnlineStatus('idle');
    } catch {
      setOnlineStatus('error');
      setInviteStatus('error');
      setInviteError('Could not connect to the invite server.');
    }
  };

  const createInviteRoom = () => {
    setInviteError(null);
    setInviteStatus('creating');
    multiplayer.createInvite();
  };

  const joinInviteRoom = (code: string) => {
    const trimmed = code.trim();
    if (trimmed.length !== 6) {
      setInviteError('Invite codes are exactly 6 characters.');
      return;
    }
    setInviteError(null);
    setInviteStatus('joining');
    multiplayer.joinInvite(trimmed);
  };

  const cancelInviteLobby = () => {
    if (inviteStatus === 'creating' || inviteStatus === 'waiting') {
      multiplayer.cancelInvite();
    }
    multiplayer.disconnect();
    setInviteStatus('idle');
    setInviteCode(null);
    setInviteJoinCode('');
    setInviteError(null);
    setOnlineKind(null);
    setGameMode(null);
    setFirstPlayer(null);
  };

  // Show the "X will move first" banner over the board. The game is already
  // mounted and running underneath; the banner auto-hides after the animation
  // (~1s) so the game then plays normally.
  const showFirstMoverOverlay = useCallback(() => {
    setFirstMoverOverlay(true);
    if (firstMoverTimerRef.current !== null) {
      window.clearTimeout(firstMoverTimerRef.current);
    }
    firstMoverTimerRef.current = window.setTimeout(() => {
      firstMoverTimerRef.current = null;
      setFirstMoverOverlay(false);
    }, FIRST_MOVER_OVERLAY_MS);
  }, []);

  const handleMainMenu = () => {
    if (discardTimerRef.current !== null) {
      window.clearTimeout(discardTimerRef.current);
      discardTimerRef.current = null;
    }
    // leaving an online match disconnects from the relay, which tells the
    // opponent the match ended
    multiplayer.disconnect();
    setDrawnIds(new Set());
    setActivatingCard(null);
    setCardMenu(null);
    setDescCard(null);
    setDiscardingCard(null);
    setDiscardFrom(null);
    setLogEntries([]);
    setFirstPlayer(null);
    setFirstMoverOverlay(false);
    if (firstMoverTimerRef.current !== null) {
      window.clearTimeout(firstMoverTimerRef.current);
      firstMoverTimerRef.current = null;
    }
    setGameMode(null);
    setOnlineKind(null);
    setOnlineStatus('idle');
    setOnlineMatch(null);
    setOpponentLeft(false);
    setInviteStatus('idle');
    setInviteCode(null);
    setInviteJoinCode('');
    setInviteError(null);
    setUiState(INITIAL_STATE);
    logRef.current = [];
    savedGameRef.current = false;
  };

  // sidebar navigation: starting fresh, a live game is abandoned (GameCanvas
  // destroys itself on unmount, so this is always safe)
  const handleSidebarNewGame = () => {
    handleMainMenu();
    setShowHistory(false);
    setSidebarOpen(false);
  };

  const handleSidebarHistory = () => {
    handleMainMenu();
    setShowHistory(true);
    setSidebarOpen(false);
  };

  const handleAuthed = (u: User) => {
    setUser(u);
    setAuthView('login');
    setShowHistory(false);
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      // the local session is cleared regardless
    }
    setUser(null);
    setShowHistory(false);
    setSidebarOpen(false);
    handleMainMenu();
  };

  // Escape closes the mobile menu drawer
  useEffect(() => {
    if (!sidebarOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  useEffect(() => {
    if (!game) {
      return;
    }
    const onDrawn = (e: { card: Card; player: number }) => {
      setDrawnIds((prev) => {
        const next = new Set(prev);
        next.add(e.card.instanceId);
        return next;
      });
      // Guaranteed cleanup: the card element can remount when the hands swap,
      // so never rely on animationend alone to clear the draw state.
      window.setTimeout(() => {
        setDrawnIds((prev) => {
          const next = new Set(prev);
          next.delete(e.card.instanceId);
          return next;
        });
      }, 1500);
    };
    const onPlayed = (e: { card: Card; player: number }) => {
      setActivatingCard({ card: e.card, player: e.player });
    };
    const onDiscarded = (e: { card: Card; player: number; delay?: number }) => {
      // the discarded card flies from its owner's hand row to the board
      // center (measured at event time, before React re-renders the hand).
      // The owner's hand may be on the bottom (the local player, or P1) or on
      // the top (the opponent / P2), depending on the mode and side.
      const handRef = e.player === bottomPlayer ? p1HandRef : p2HandRef;
      const handRect = handRef.current?.getBoundingClientRect();
      const areaRect = boardAreaRef.current?.getBoundingClientRect();
      if (handRect && areaRect) {
        setDiscardFrom({
          x: handRect.left + handRect.width / 2 - (areaRect.left + areaRect.width / 2),
          y: handRect.top + handRect.height / 2 - (areaRect.top + areaRect.height / 2),
        });
      } else {
        setDiscardFrom(null);
      }
      // show the discard reveal after the optional activation overlay (THROW
      // CARD delays it; a voluntary discard reveals immediately)
      const revealDelayMs = e.delay ?? DISCARD_REVEAL_DELAY_MS;
      if (discardTimerRef.current !== null) {
        window.clearTimeout(discardTimerRef.current);
      }
      discardTimerRef.current = window.setTimeout(() => {
        discardTimerRef.current = null;
        setDiscardingCard(e.card);
      }, revealDelayMs);
    };
    const onLog = (entry: GameLogEntry) => {
      logRef.current = [...logRef.current, entry];
      setLogEntries(logRef.current);
    };
    game.events.on('card-drawn', onDrawn);
    game.events.on('card-played', onPlayed);
    game.events.on('card-discarded', onDiscarded);
    game.events.on('game-log', onLog);
    return () => {
      if (discardTimerRef.current !== null) {
        window.clearTimeout(discardTimerRef.current);
        discardTimerRef.current = null;
      }
      game.events.off('card-drawn', onDrawn);
      game.events.off('card-played', onPlayed);
      game.events.off('card-discarded', onDiscarded);
      game.events.off('game-log', onLog);
    };
  }, [game, bottomPlayer]);

  // online multiplayer: forward relayed actions into the Phaser scene and turn
  // match/opponent events into app state
  useEffect(() => {
    const onAction = (a: unknown) => {
      gameRef.current?.events.emit('online-action', a as OnlineAction);
    };
    const onMatchStart = (m: unknown) => {
      const match = m as MatchStart;
      setOnlineMatch(match);
      setOnlineStatus('idle');
      setFirstPlayer(match.firstPlayer);
      // show the "who moves first" banner over the board before play begins
      showFirstMoverOverlay();
      setGameMode('online');
      setUiState((prev) => ({ ...prev, gameMode: 'online', botPlayer: null }));
      setInviteStatus('idle');
      setInviteCode(null);
      setInviteJoinCode('');
      setInviteError(null);
    };
    const onInviteCreated = (data: unknown) => {
      const { code } = data as { code: string };
      setInviteCode(code);
      setInviteStatus('waiting');
    };
    const onInviteError = (data: unknown) => {
      const { message } = data as { message: string };
      setInviteError(message);
      // keep the join form on screen so the guest can re-check the code
      setInviteStatus((prev) => (prev === 'joining' ? 'join' : prev));
    };
    const onOpponentLeft = () => {
      // an already-finished game does not need the disconnect overlay (the
      // player is leaving from the winner screen anyway)
      if (uiStateRef.current.gameOver) {
        return;
      }
      setOpponentLeft(true);
    };
    const onDisconnect = () => {
      // a dropped connection is only actionable while the player is still in
      // the lobby (a mid-match disconnect surfaces as opponent-left instead)
      setOnlineStatus((prev) => (prev === 'waiting' || prev === 'connecting' ? 'error' : prev));
      if (onlineKindRef.current === 'invite' && (inviteStatusRef.current === 'waiting' || inviteStatusRef.current === 'creating' || inviteStatusRef.current === 'joining')) {
        setInviteStatus('error');
        setInviteError('Connection lost. Please try again.');
      }
    };
    multiplayer.on('action', onAction);
    multiplayer.on('match-start', onMatchStart);
    multiplayer.on('invite-created', onInviteCreated);
    multiplayer.on('invite-error', onInviteError);
    multiplayer.on('opponent-left', onOpponentLeft);
    multiplayer.on('disconnect', onDisconnect);
    return () => {
      multiplayer.off('action', onAction);
      multiplayer.off('match-start', onMatchStart);
      multiplayer.off('invite-created', onInviteCreated);
      multiplayer.off('invite-error', onInviteError);
      multiplayer.off('opponent-left', onOpponentLeft);
      multiplayer.off('disconnect', onDisconnect);
    };
  }, [showFirstMoverOverlay]);

  // restore the session on boot; a loading state is shown until then
  useEffect(() => {
    api
      .me()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  // save the finished game once to the signed-in user's history
  useEffect(() => {
    if (!user) {
      return;
    }
    if (uiState.gameOver) {
      if (savedGameRef.current) {
        return;
      }
      savedGameRef.current = true;
      // `localPlayer` is the side this client controls: null in local 2-player
      // (both players share the account, so it is recorded as side 1), 1 vs the
      // bot, and the assigned side in online matches
      const side = uiState.localPlayer ?? 1;
      const result = uiState.draw ? 'draw' : uiState.winner === side ? 'win' : 'loss';
      const mode = uiState.gameMode === 'bot' ? 'vs-bot' : uiState.gameMode === 'online' ? 'online' : '2p';
      api.saveGame({ mode, result, side, moves: logRef.current }).catch(() => {
        // history is best-effort; a failed save must not block the game
      });
    } else {
      savedGameRef.current = false;
    }
  }, [user, uiState.gameOver, uiState.draw, uiState.winner, uiState.gameMode, uiState.localPlayer]);

  // only the canvas frame scales; everything else is responsive DOM.
  // useLayoutEffect measures before paint so the board never flashes at 100%.
  useLayoutEffect(() => {
    const measure = () => {
      // the deck/info/history chrome now lives in a fixed drawer, so the board
      // gets the full viewport; pads and gaps shrink on small screens so the
      // board keeps as much room as possible
      const isNarrow = window.innerWidth < 640;
      const pad = Math.min(APP_PADDING, Math.max(8, window.innerWidth * 0.03));
      const rowGap = isNarrow ? 10 : ROW_GAP;
      const stackGap = isNarrow ? 10 : STACK_GAP;
      const topHandH = p2HandRef.current?.offsetHeight ?? 0;
      const bottomHandH = p1HandRef.current?.offsetHeight ?? 0;
      // on phones the sidebar is a hidden overlay drawer, so the board gets
      // the full viewport width
      const sidebarW = isNarrow ? 0 : SIDEBAR_WIDTH;
      const availW = window.innerWidth - sidebarW - pad * 2 - rowGap;
      const availH = window.innerHeight - topHandH - bottomHandH - stackGap * 2 - pad * 2;
      const s = Math.min(1, availW / CANVAS_SIZE, availH / CANVAS_SIZE);
      setCanvasScale(Math.max(0.25, s));

      // float the deck beside the board on PC: right of the board, vertically
      // centered with it, and never glued to the window edge. Phones ignore
      // these vars via a CSS media query (classic bottom-right corner FAB).
      const boardRect = boardAreaRef.current?.getBoundingClientRect();
      if (boardRect) {
        const fabSize = 72;
        const gap = 14;
        const fabLeft = Math.min(
          boardRect.right + gap,
          Math.max(8, window.innerWidth - fabSize - 8)
        );
        const fabTop = boardRect.top + boardRect.height / 2;
        document.documentElement.style.setProperty('--fab-left', `${fabLeft}px`);
        document.documentElement.style.setProperty('--fab-top', `${fabTop}px`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) {
      [boardAreaRef.current, p2HandRef.current, p1HandRef.current].forEach((el) => {
        if (el) {
          ro.observe(el);
        }
      });
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
    // re-measure once the game mounts (the board area appears right when the
    // mode/match starts; the "X will move first" banner is absolutely
    // positioned so it does not affect the layout)
  }, [gameMode, firstPlayer]);

  const handleCardClick = (player: number, card: Card, e: React.MouseEvent<HTMLElement>) => {
    if (
      uiState.gameOver ||
      uiState.targeting ||
      player !== currentPlayer ||
      uiState.botPlayer === player ||
      // in single-side modes (vs bot / online) only your own hand is clickable;
      // the opponent's face-down cards never open a menu
      (uiState.localPlayer !== null && player !== uiState.localPlayer) ||
      // the card menu stays closed while a discard reveal is playing
      discardingCard !== null
    ) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setCardMenu({ player, card, x: rect.left + rect.width / 2, y: rect.top });
  };

  const openDescription = (menu: CardMenu) => {
    setDescFrom({ x: menu.x, y: menu.y });
    setDescCard(menu.card);
    setCardMenu(null);
  };

  const activateCard = (menu: CardMenu) => {
    gameRef.current?.events.emit('card-activate', menu.card.instanceId);
    setCardMenu(null);
  };

  const discardCard = (menu: CardMenu) => {
    gameRef.current?.events.emit('card-discard-requested', menu.card.instanceId);
    setCardMenu(null);
  };

  const handleExport = () => {
    const text = serializeLog(logEntries);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `taytaya-log-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderHistory = () => {
    const nodes: React.ReactNode[] = [];
    let lastMove = 0;
    logEntries.forEach((entry, idx) => {
      if (entry.move !== lastMove) {
        lastMove = entry.move;
        nodes.push(
          <div key={`move-${idx}`} className="history-panel__move">
            Move {entry.move}
          </div>
        );
      }
      nodes.push(
        <div key={idx} className="history-panel__line">
          {formatEntry(entry)}
        </div>
      );
    });
    if (verbose) {
      nodes.push(
        <pre key="verbose" className="history-panel__diagram">
          {renderBoard(uiState.board, uiState.lockedCells)}
          {`\nP1 hand: ${uiState.hand1.length} · P2 hand: ${uiState.hand2.length} · deck: ${uiState.deckCount}`}
        </pre>
      );
    }
    return nodes;
  };

  const currentPlayer = uiState.currentPlayer;
  const opponentHandCount = currentPlayer === 1 ? uiState.hand2.length : uiState.hand1.length;
  const frameSize = CANVAS_SIZE * canvasScale;

  const renderHand = (
    player: number,
    cards: Card[],
    position: 'top' | 'bottom',
    ref: React.RefObject<HTMLDivElement>
  ) => {
    const isActivePlayer = !uiState.gameOver && player === currentPlayer && !uiState.targeting && uiState.botPlayer !== player;
    // Face-down rule: only the OPPONENT's hand is ever face-down. In single-side
    // modes (vs bot / online) your own cards stay face-up even on the
    // opponent's turn; in shared-screen 2-player the inactive player's hand is
    // hidden instead.
    const local = uiState.localPlayer;
    const isFaceDown =
      uiState.botPlayer === player ||
      (local === null && !isActivePlayer) ||
      (local !== null && player !== local);
    const name = uiState.playerNames[player] ?? `Player ${player}`;
    const turnSuffix =
      player === currentPlayer && !uiState.gameOver ? (name === 'You' ? ' — your turn' : "'s turn") : '';
    return (
      <div className={`hand hand--${position} ${isActivePlayer ? '' : 'hand--inactive'}`} ref={ref}>
        <span
          className={`hand__label player-${player} ${
            player === currentPlayer && !uiState.gameOver ? 'hand-turn-float' : ''
          }`}
        >
          <span className="hand__label-name">
            {name}
            {uiState.botPlayer === player ? ' (BOT)' : ''}
            {turnSuffix}
          </span>
          {uiState.cardLockedPlayer === player && (
            <span className="hand__label-lock" title="Cards locked - cannot activate any card">
              <svg className="hand__lock-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2zm-5 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM10 7v2h4V7a2 2 0 0 0-4 0z" />
              </svg>
              Cards locked
            </span>
          )}
        </span>
        <div className="hand__cards">
          {cards.map((card, i) => {
          const cardType = CARD_TYPES[card.id];
          const isTargeting = uiState.targeting && uiState.targetingCardId === card.instanceId;
          const isDrawn = drawnIds.has(card.instanceId);
          return (
            <div
              key={`${card.instanceId}-${isDrawn ? 'drawn' : 'plain'}`}
              className={[
                'hand-card',
                isDrawn ? 'hand-card--drawn' : '',
                isTargeting ? 'hand-card--targeting' : '',
                isActivePlayer ? '' : 'hand-card--inactive',
                isFaceDown ? 'hand-card--face-down' : '',
                uiState.chainableCardId === card.instanceId ? 'hand-card--chainable' : '',
                uiState.leavingCardId === card.instanceId ? 'hand-card--leaving' : '',
              ].join(' ')}
              style={{ '--i': i } as React.CSSProperties}
              onClick={(e) => handleCardClick(player, card, e)}
              onAnimationEnd={() => {
                if (isDrawn) {
                  setDrawnIds((prev) => {
                    const next = new Set(prev);
                    next.delete(card.instanceId);
                    return next;
                  });
                }
              }}
              title={isFaceDown ? undefined : cardType.name}
            >
              <div className="hand-card__flip">
                <div className="hand-card__inner">
                  <div className="hand-card__face hand-card__front">
                    <img src={CARD_ARTS[cardType.artKey]} alt={cardType.name} className="hand-card__art" />
                  </div>
                  <div className="hand-card__face hand-card__back">
                    <img src={cardBackArt} alt="Card back" className="hand-card__art" />
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="App">
        <p className="loading">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        {authView === 'login' ? (
          <LoginPage onAuthed={handleAuthed} onSwitch={() => setAuthView('signup')} />
        ) : (
          <SignupPage onAuthed={handleAuthed} onSwitch={() => setAuthView('login')} />
        )}
      </div>
    );
  }

  // display name of the player who will move first, shown in the banner over
  // the board. Real usernames for online/invite matches ("Userxxxx will move
  // first"), "You"/"BOT" vs the bot, and "Player 1"/"Player 2" hotseat.
  const firstMoverName =
    gameMode === 'online' && onlineMatch
      ? firstPlayer === onlineMatch.side
        ? user.username
        : onlineMatch.opponent
      : gameMode === 'bot'
        ? firstPlayer === 1
          ? user.username
          : 'BOT'
        : `Player ${firstPlayer}`;

  return (
    <div className="App App--with-sidebar">
      <button
        type="button"
        className={`sidebar-toggle ${sidebarOpen ? 'sidebar-toggle--open' : ''}`}
        onClick={() => setSidebarOpen((open) => !open)}
        aria-expanded={sidebarOpen}
        aria-controls="sidebar"
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
      >
        <span className="sidebar-toggle__line" />
        <span className="sidebar-toggle__line" />
        <span className="sidebar-toggle__line" />
      </button>

      <Sidebar
        user={user}
        title={GAME_TITLE}
        active={showHistory ? 'history' : 'new-game'}
        onNewGame={handleSidebarNewGame}
        onHistory={handleSidebarHistory}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div
        className={`sidebar-backdrop ${sidebarOpen ? 'sidebar-backdrop--visible' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <main className="app-content">
        {showHistory ? (
          <HistoryPage user={user} onBack={() => setShowHistory(false)} onLogout={handleLogout} />
        ) : gameMode === null ? (
          <div className="mode-menu">
            <p className="mode-menu__subtitle">Choose a game mode</p>
            <button
              className="pill-button pill-button--accent"
              onClick={() => handleModeSelect('pvp')}
            >
              2 Players
            </button>
            <button
              className="pill-button pill-button--accent"
              onClick={() => handleModeSelect('bot')}
            >
              Vs Bot
            </button>
            <button
              className="pill-button pill-button--accent"
              onClick={() => handleModeSelect('online')}
            >
              Online Multiplayer
            </button>
            <button
              className="pill-button pill-button--accent"
              onClick={startInviteLobby}
            >
              Invite Match
            </button>
          </div>
        ) : gameMode === 'online' && !onlineMatch ? (
          onlineKind === 'invite' ? (
            <div className="online-lobby">
              {onlineStatus === 'connecting' && (
                <p className="mode-menu__subtitle">Connecting…</p>
              )}
              {inviteStatus === 'idle' && !inviteError && onlineStatus !== 'connecting' && (
                <>
                  <p className="mode-menu__subtitle">Invite a friend for a private match.</p>
                  <div className="invite-actions">
                    <button className="pill-button pill-button--accent" onClick={createInviteRoom}>
                      Create a room
                    </button>
                    <button className="pill-button pill-button--accent" onClick={() => setInviteStatus('join')}>
                      Join with a code
                    </button>
                  </div>
                  <button className="pill-button" onClick={cancelInviteLobby}>
                    Back
                  </button>
                </>
              )}
              {inviteStatus === 'creating' && (
                <>
                  <p className="mode-menu__subtitle">Creating your room…</p>
                  <button className="pill-button" onClick={cancelInviteLobby}>
                    Cancel
                  </button>
                </>
              )}
              {inviteStatus === 'waiting' && inviteCode && (
                <>
                  <p className="mode-menu__subtitle">Your invite code</p>
                  <p className="invite-code">{inviteCode}</p>
                  <p className="mode-menu__subtitle">
                    Share this code with a friend — waiting for them to join…
                  </p>
                  <div className="online-lobby__spinner" aria-hidden="true" />
                  <button className="pill-button" onClick={cancelInviteLobby}>
                    Cancel
                  </button>
                </>
              )}
              {(inviteStatus === 'join' || inviteStatus === 'joining') && (
                <form
                  className="invite-join"
                  onSubmit={(e) => {
                    e.preventDefault();
                    joinInviteRoom(inviteJoinCode);
                  }}
                >
                  <label className="invite-join__label" htmlFor="invite-code-input">
                    Enter the 6-character invite code
                  </label>
                  <input
                    id="invite-code-input"
                    className="invite-join__input"
                    value={inviteJoinCode}
                    onChange={(e) => setInviteJoinCode(e.target.value)}
                    maxLength={6}
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="e.g. aB3xY9"
                  />
                  {inviteError && <p className="online-lobby__error">{inviteError}</p>}
                  <div className="invite-actions">
                    <button
                      type="submit"
                      className="pill-button pill-button--accent"
                      disabled={inviteStatus === 'joining'}
                    >
                      {inviteStatus === 'joining' ? 'Joining…' : 'Join'}
                    </button>
                    <button type="button" className="pill-button" onClick={cancelInviteLobby}>
                      Back
                    </button>
                  </div>
                </form>
              )}
              {inviteStatus === 'error' && (
                <>
                  <p className="online-lobby__error">
                    {inviteError ?? 'Something went wrong. Please try again.'}
                  </p>
                  <button className="pill-button" onClick={cancelInviteLobby}>
                    Back
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="online-lobby">
              {onlineStatus === 'error' ? (
                <>
                  <p className="mode-menu__subtitle">Could not connect to the matchmaking server.</p>
                  <button className="pill-button pill-button--accent" onClick={startOnlineLobby}>
                    Try again
                  </button>
                  <button className="pill-button" onClick={cancelOnlineLobby}>
                    Back
                  </button>
                </>
              ) : (
                <>
                  <p className="mode-menu__subtitle">Waiting for an opponent…</p>
                  <div className="online-lobby__spinner" aria-hidden="true" />
                  <button className="pill-button" onClick={cancelOnlineLobby}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          )
        ) : (
          <div className="page-row">
        <div className="board-area" ref={boardAreaRef}>
          {firstMoverOverlay && (
            <div className="first-mover-overlay">
              <div className="first-mover-overlay__card">
                <span className="first-mover-overlay__name">{firstMoverName}</span>
                <span>will move first</span>
              </div>
            </div>
          )}
          <TimerBadges game={game} botPlayer={uiState.botPlayer} playerNames={onlinePlayerNames} localPlayer={uiState.localPlayer} />
          {/* The local player's table is always on the bottom side; the
              opponent's is at the top. In local 2-player that is P1/P2 as
              before; online, side 2's board is flipped by the scene. */}
          {renderHand(topPlayer, topPlayer === 1 ? uiState.hand1 : uiState.hand2, 'top', p2HandRef)}

          <div
            className="canvas-frame"
            style={{ width: frameSize, height: frameSize }}
          >
            <div className="canvas-inner">
              <div
                className="canvas-scaled"
                style={{ transform: `scale(${canvasScale})` }}
              >
                <GameCanvas
                  key={`${gameMode}-${firstPlayer}-${onlineMatch?.room ?? 'local'}`}
                  gameMode={gameMode}
                  firstPlayer={firstPlayer ?? 1}
                  initialDeck={onlineMatch?.deck}
                  initialHands={onlineMatch?.hands}
                  localPlayer={localSide}
                  sendAction={isOnlineGame ? (a) => multiplayer.sendAction(a) : undefined}
                  playerNames={onlinePlayerNames}
                  onStateChange={setUiState}
                  onReady={handleReady}
                />
              </div>
            </div>

            {uiState.gameOver && (
              <div className="winner-overlay">
                <div className="winner-overlay__card">
                  <p className="winner-overlay__text">
                    {uiState.draw
                      ? 'Draw! No legal moves left.'
                      : uiState.winner === uiState.botPlayer
                        ? 'BOT wins!'
                        : uiState.winner === uiState.localPlayer
                          ? 'You win!'
                          : `${uiState.playerNames[uiState.winner as number] ?? `Player ${uiState.winner}`} wins! 🎉`}
                  </p>
                  {gameMode !== 'online' && (
                    <button className="pill-button" onClick={handleRestart}>
                      Restart
                    </button>
                  )}
                  <button className="pill-button" onClick={handleMainMenu}>
                    Main menu
                  </button>
                </div>
              </div>
            )}
          </div>

          {renderHand(bottomPlayer, bottomPlayer === 1 ? uiState.hand1 : uiState.hand2, 'bottom', p1HandRef)}

          {/* centered over the board area (its center is exactly the canvas
              center because both hands are equal-height), not the viewport,
              which the side panel would shift right */}
          {activatingCard && (
            <div className="card-activate-overlay">
              <div
                className="card-activate"
                key={activatingCard.card.instanceId}
                onAnimationEnd={(e) => {
                  if (e.target === e.currentTarget) {
                    setActivatingCard(null);
                  }
                }}
              >
                <div className="card-activate__flip">
                  <div className="card-activate__face card-activate__front">
                    <img src={CARD_ARTS[CARD_TYPES[activatingCard.card.id].artKey]} alt={CARD_TYPES[activatingCard.card.id].name} />
                  </div>
                  <div className="card-activate__face card-activate__back">
                    <img src={cardBackArt} alt="" />
                  </div>
                </div>
                <span className="card-activate__label">ACTIVATE!</span>
              </div>
            </div>
          )}

          {discardingCard && (
            <div className="card-discard-overlay">
              <div
                className="card-discard"
                key={discardingCard.instanceId}
                style={{
                  '--from-x': discardFrom ? `${discardFrom.x}px` : '0px',
                  '--from-y': discardFrom ? `${discardFrom.y}px` : '0px',
                } as React.CSSProperties}
                onAnimationEnd={(e) => {
                  if (e.target === e.currentTarget) {
                    setDiscardingCard(null);
                  }
                }}
              >
                <img src={CARD_ARTS[CARD_TYPES[discardingCard.id].artKey]} alt={CARD_TYPES[discardingCard.id].name} />
                <span className="card-discard__label">DISCARDED!</span>
              </div>
            </div>
          )}
        </div>

        {/* the floating deck is the drawer toggle: one element, two jobs */}
        <button
          type="button"
          className={`deck-fab ${drawerOpen ? 'deck-fab--open' : ''}`}
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
          aria-controls="history-drawer"
          aria-label={drawerOpen ? 'Close game info' : 'Open game info'}
        >
          <img src={cardBackArt} alt="" className="deck-fab__art" />
          <span className="deck-fab__count">{uiState.deckCount}</span>
        </button>
          </div>
        )}
      </main>

      <div
        className={`drawer-backdrop ${drawerOpen ? 'drawer-backdrop--visible' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />

      <aside
        id="history-drawer"
        className={`history-drawer ${drawerOpen ? 'history-drawer--open' : ''}`}
        aria-hidden={!drawerOpen}
      >
        <div className="history-drawer__header">
          <span className="history-drawer__title">GAME</span>
          <button
            type="button"
            className="history-drawer__close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="info-panel">
          <p>Deck: {uiState.deckCount}</p>
          <p>Discard: {uiState.discardCount}</p>
          <p>Locked: {uiState.lockedCells.length}</p>
          {uiState.cardLockedPlayer !== null && (
            <p className="info-panel__lock">Player {uiState.cardLockedPlayer}'s cards locked</p>
          )}
          {uiState.targeting && (
            <p className="info-panel__hint">Pick an empty cell to lock!</p>
          )}
        </div>

        <div className="history-panel">
          <div className="history-panel__header">
            <span className="history-panel__title">HISTORY</span>
            <button className="history-panel__export" onClick={handleExport}>
              Export .txt
            </button>
          </div>
          <label className="history-panel__verbose">
            <input
              type="checkbox"
              checked={verbose}
              onChange={(e) => setVerbose(e.target.checked)}
            />
            verbose (board view)
          </label>
          <div className="history-panel__body">
            {logEntries.length === 0 ? (
              <p className="history-panel__empty">No moves yet</p>
            ) : (
              renderHistory()
            )}
          </div>
        </div>
      </aside>

      {cardMenu && (
        <>
          <div className="card-menu-backdrop" onClick={() => setCardMenu(null)} />
          <div className="card-menu" style={{ left: cardMenu.x, top: cardMenu.y }}>
            <div className="card-menu__preview">
              <img src={CARD_ARTS[CARD_TYPES[cardMenu.card.id].artKey]} alt={CARD_TYPES[cardMenu.card.id].name} />
            </div>
            <p className="card-menu__name">{CARD_TYPES[cardMenu.card.id].name}</p>
            <button
              className="pill-button pill-button--small"
              onClick={() => openDescription(cardMenu)}
            >
              Show description
            </button>
            {cardMenu.player === currentPlayer &&
              (cardMenu.card.instanceId === uiState.chainableCardId || !uiState.cardPlayedThisTurn) &&
              cardMenu.player !== uiState.cardLockedPlayer &&
              !(cardMenu.card.id === 'throw-card' && opponentHandCount === 0) && (
              <button
                className="pill-button pill-button--small pill-button--accent"
                onClick={() => activateCard(cardMenu)}
              >
                Activate
              </button>
            )}
            {cardMenu.player === currentPlayer && !uiState.cardPlayedThisTurn && !uiState.cardDiscardedThisTurn && (
              <button
                className="pill-button pill-button--small"
                onClick={() => discardCard(cardMenu)}
              >
                Discard
              </button>
            )}
          </div>
        </>
      )}

      {descCard && (
        <div className="card-modal" onClick={() => setDescCard(null)}>
          <div className="card-modal__panel" onClick={(e) => e.stopPropagation()}>
            <div
              className="card-modal__card"
              style={{
                '--from-x': descFrom
                  ? `${descFrom.x - window.innerWidth / 2}px`
                  : '0px',
                '--from-y': descFrom
                  ? `${descFrom.y - window.innerHeight / 2}px`
                  : '0px',
              } as React.CSSProperties}
            >
              <img src={CARD_ARTS[CARD_TYPES[descCard.id].artKey]} alt={CARD_TYPES[descCard.id].name} />
            </div>
            <h2 className="card-modal__name">{CARD_TYPES[descCard.id].name}</h2>
            <p className="card-modal__desc">{CARD_TYPES[descCard.id].description}</p>
            <button className="pill-button" onClick={() => setDescCard(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {opponentLeft && (
        <div className="opponent-left-overlay">
          <div className="opponent-left-overlay__card">
            <p className="opponent-left-overlay__text">Your opponent left the game.</p>
            <button
              className="pill-button pill-button--accent"
              onClick={() => {
                setOpponentLeft(false);
                handleMainMenu();
              }}
            >
              Main menu
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
